import { sanitizeTgHtml } from "@kickwin/shared";

/**
 * Between the rich-text editor and Telegram HTML (decision 45).
 *
 * Telegram HTML has no paragraphs: a line break is a newline character, and
 * the only blocks are quotes and code blocks. The editor thinks in
 * paragraphs. So a message goes into the editor as one paragraph per line,
 * and comes back out by walking the editor's document, one block per line —
 * never through the browser's HTML, which would bring `<p>` and `&nbsp;`
 * with it.
 */

export function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Plain text saved before formatting existed, as Telegram HTML. */
export function plainToTgHtml(text: string): string {
  return escapeHtml(text);
}

const lines = (html: string) =>
  html
    .split("\n")
    .map((line) => `<p>${line}</p>`)
    .join("");

/** Telegram HTML as the editor's starting content. */
export function tgHtmlToEditor(html: string): string {
  const clean = sanitizeTgHtml(html);
  const block = /<pre>[\s\S]*?<\/pre>|<blockquote( expandable)?>([\s\S]*?)<\/blockquote>/g;
  let out = "";
  let last = 0;
  let afterBlock = false;
  for (let m = block.exec(clean); m; m = block.exec(clean)) {
    // The newline on either side of a block is the break around it, not an
    // empty line of its own.
    let before = clean.slice(last, m.index);
    if (afterBlock) before = before.replace(/^\n/, "");
    before = before.replace(/\n$/, "");
    if (before || (!afterBlock && m.index > 0)) out += lines(before);
    out += m[0].startsWith("<pre>")
      ? m[0]
      : `<blockquote${m[1] ?? ""}>${lines(m[2] ?? "")}</blockquote>`;
    last = m.index + m[0].length;
    afterBlock = true;
  }
  let rest = clean.slice(last);
  if (afterBlock) rest = rest.replace(/^\n/, "");
  if (rest || !afterBlock) out += lines(rest);
  return out;
}

// --- editor document → Telegram HTML -----------------------------------------

interface Mark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface DocNode {
  type: string;
  text?: string;
  marks?: Mark[];
  attrs?: Record<string, unknown>;
  content?: DocNode[];
}

/** Outermost first; code last, because Telegram lets nothing nest in code. */
const MARK_ORDER = ["link", "bold", "italic", "underline", "strike", "spoiler", "code"];
const MARK_TAG: Record<string, string> = {
  bold: "b",
  italic: "i",
  underline: "u",
  strike: "s",
  spoiler: "tg-spoiler",
  code: "code",
};

function inline(nodes: DocNode[] | undefined): string {
  let out = "";
  for (const node of nodes ?? []) {
    if (node.type === "hardBreak") {
      out += "\n";
      continue;
    }
    if (node.type !== "text" || !node.text) continue;
    const marks = [...(node.marks ?? [])].sort(
      (a, b) => MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type),
    );
    let open = "";
    let close = "";
    for (const mark of marks) {
      if (mark.type === "link") {
        const href = String(mark.attrs?.href ?? "");
        open += `<a href="${href.replace(/&/g, "&amp;").replace(/"/g, "&quot;")}">`;
        close = `</a>${close}`;
      } else if (MARK_TAG[mark.type]) {
        open += `<${MARK_TAG[mark.type]}>`;
        close = `</${MARK_TAG[mark.type]}>${close}`;
      }
    }
    out += open + escapeHtml(node.text) + close;
  }
  return out;
}

function block(node: DocNode): string | null {
  switch (node.type) {
    case "paragraph":
      return inline(node.content);
    case "blockquote": {
      const inner = (node.content ?? []).map(block).filter((b) => b !== null);
      const tag = node.attrs?.expandable ? "<blockquote expandable>" : "<blockquote>";
      return `${tag}${inner.join("\n")}</blockquote>`;
    }
    case "codeBlock": {
      const text = escapeHtml((node.content ?? []).map((n) => n.text ?? "").join(""));
      const language = String(node.attrs?.language ?? "").trim();
      return language
        ? `<pre><code class="language-${language}">${text}</code></pre>`
        : `<pre>${text}</pre>`;
    }
    default:
      return inline(node.content);
  }
}

/** The editor's document as Telegram HTML. */
export function editorToTgHtml(doc: DocNode): string {
  const blocks = (doc.content ?? []).map(block).filter((b) => b !== null);
  // The empty line the editor keeps under the text is not part of it.
  while (blocks.length > 0 && blocks.at(-1) === "") blocks.pop();
  return sanitizeTgHtml(blocks.join("\n"));
}
